/**
 * Responsive breakpoint utilities for FunnelExperience block rendering.
 *
 * Provides:
 *  - Breakpoint type and BREAKPOINTS constant
 *  - useBreakpoint() — reactive hook via window.matchMedia (no resize listener)
 *  - resolveResponsiveProps() — pure cascading merge of base props + overrides
 */

import { useState, useEffect } from 'react';
import type { JsonObject } from '@trafilea/tss-lib-funnel-schema-ts';

/** Responsive prop overrides for page-builder blocks (per breakpoint). */
export interface BlockResponsiveOverrides {
  tablet?: JsonObject;
  mobileLandscape?: JsonObject;
  mobilePortrait?: JsonObject;
}

// ---------------------------------------------------------------------------
// Types & Constants
// ---------------------------------------------------------------------------

export type Breakpoint = 'desktop' | 'tablet' | 'mobile-landscape' | 'mobile-portrait';

/**
 * Maximum-width breakpoints (px) matching common device categories.
 * - tablet:           screens ≤ 1024 px wide
 * - mobileLandscape:  screens ≤ 768 px wide
 * - mobilePortrait:   screens ≤ 480 px wide
 *
 * Evaluation order (widest to narrowest): each breakpoint subsumes the previous.
 */
export const BREAKPOINTS = {
  tablet: 1024,
  mobileLandscape: 768,
  mobilePortrait: 480,
} as const;

// ---------------------------------------------------------------------------
// useBreakpoint
// ---------------------------------------------------------------------------

/**
 * Reactive hook that returns the current breakpoint based on the viewport width.
 *
 * Uses window.matchMedia with 'change' event listeners — no resize polling.
 * Re-evaluates when any media query fires a change event.
 *
 * Cascade (widest first):
 *   > 1024px            → 'desktop'
 *   769px – 1024px      → 'tablet'
 *   481px – 768px       → 'mobile-landscape'
 *   ≤ 480px             → 'mobile-portrait'
 */
export function useBreakpoint(): Breakpoint {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(computeBreakpoint);

  useEffect(() => {
    // Guard for environments without matchMedia (SSR, jsdom-based tests, older
    // browsers). computeBreakpoint() still provides a sensible initial value.
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const portraitQuery = window.matchMedia(`(max-width: ${BREAKPOINTS.mobilePortrait}px)`);
    const landscapeQuery = window.matchMedia(`(max-width: ${BREAKPOINTS.mobileLandscape}px)`);
    const tabletQuery = window.matchMedia(`(max-width: ${BREAKPOINTS.tablet}px)`);

    function handleChange() {
      setBreakpoint(computeBreakpoint());
    }

    portraitQuery.addEventListener('change', handleChange);
    landscapeQuery.addEventListener('change', handleChange);
    tabletQuery.addEventListener('change', handleChange);

    return () => {
      portraitQuery.removeEventListener('change', handleChange);
      landscapeQuery.removeEventListener('change', handleChange);
      tabletQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return breakpoint;
}

/** Compute the breakpoint from the current window width. SSR-safe (returns 'desktop' if no window). */
function computeBreakpoint(): Breakpoint {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w <= BREAKPOINTS.mobilePortrait) return 'mobile-portrait';
  if (w <= BREAKPOINTS.mobileLandscape) return 'mobile-landscape';
  if (w <= BREAKPOINTS.tablet) return 'tablet';
  return 'desktop';
}

// ---------------------------------------------------------------------------
// resolveResponsiveProps
// ---------------------------------------------------------------------------

/**
 * Merge base block props with per-breakpoint overrides for the active breakpoint.
 *
 * Cascade rules (smaller inherits from larger):
 *  - desktop          → base props only (no overrides)
 *  - tablet           → { ...base, ...overrides.tablet }
 *  - mobile-landscape → { ...base, ...overrides.tablet, ...overrides.mobileLandscape }
 *  - mobile-portrait  → { ...base, ...overrides.tablet, ...overrides.mobileLandscape, ...overrides.mobilePortrait }
 *
 * This is a pure function — it has no side effects and does not call hooks.
 *
 * @param baseProps  The block's base props (desktop-first).
 * @param overrides  The block's responsiveOverrides (may be undefined or partially set).
 * @param breakpoint The active breakpoint from useBreakpoint().
 * @returns          Merged props for the active breakpoint.
 */
export function resolveResponsiveProps(
  baseProps: JsonObject,
  overrides: BlockResponsiveOverrides | undefined,
  breakpoint: Breakpoint
): JsonObject {
  if (breakpoint === 'desktop' || !overrides) return baseProps;

  switch (breakpoint) {
    case 'tablet':
      return { ...baseProps, ...overrides.tablet };

    case 'mobile-landscape':
      return { ...baseProps, ...overrides.tablet, ...overrides.mobileLandscape };

    case 'mobile-portrait':
      return {
        ...baseProps,
        ...overrides.tablet,
        ...overrides.mobileLandscape,
        ...overrides.mobilePortrait,
      };
  }
}
